/**
 * Lobby (REDESIGN.md 7.2): the game code, big; copy invite link; seats as
 * medallion + name; "+" tiles add a computer player; host tees off.
 */
import { useEffect, useRef, useState } from 'react';
import { BOT_LEVELS, MAX_PLAYERS, MIN_PLAYERS } from '../../worker/protocol';
import { HOLE_CHOICES, LEVEL_LABEL } from '../brand';
import { PlayerMedallion } from '../components/Art';
import { IconClose, IconCopy, IconPlus, IconShare } from '../components/Icons';
import { useToast } from '../components/Toasts';
import type { RoomSnapshot } from '../net/roomClient';
import type { RoomClient } from '../net/roomClient';
import { KEYS, load, save } from '../lib/storage';

function AddSeat({ disabled, pulse, onAdd }: { disabled: boolean; pulse: boolean; onAdd: (l: (typeof BOT_LEVELS)[number]) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', off, true);
    return () => window.removeEventListener('pointerdown', off, true);
  }, [open]);
  return (
    <li className={`seat open${pulse ? ' pulse' : ''}`} ref={ref}>
      <button type="button" className="seat-add" aria-label="Add computer player" title="Add computer player" disabled={disabled} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <IconPlus size={22} />
      </button>
      {open && (
        <div className="level-pop" role="menu">
          {BOT_LEVELS.map((l) => (
            <button
              key={l}
              type="button"
              role="menuitem"
              className="level-btn"
              onClick={() => {
                onAdd(l);
                setOpen(false);
              }}
            >
              {LEVEL_LABEL[l]}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

export function Lobby({ snap, client, onHome }: { snap: RoomSnapshot; client: RoomClient; onHome: () => void }) {
  const toast = useToast();
  const [holes, setHoles] = useState<number>(() => {
    const h = load<number>(KEYS.onlineHoles, 9);
    return (HOLE_CHOICES as readonly number[]).includes(h) ? h : 9;
  });
  const { room, you, host } = snap;
  const isHost = you !== null && you === host;
  const link = `${location.origin}/r/${room.code}`;
  const seats = room.seats;
  const enough = seats.length >= MIN_PLAYERS;
  const full = seats.length >= MAX_PLAYERS;

  const copy = () =>
    navigator.clipboard?.writeText(link).then(
      () => toast('Invite link copied.'),
      () => toast(link),
    );
  const share = () => {
    void navigator.share?.({ title: 'Ancient Nine', text: `Join my Ancient Nine game: ${room.code}`, url: link }).catch(() => {});
  };

  return (
    <main className="lobby">
      <div className="lobby-card panel">
        <h1 className="lobby-code num" aria-label={`Game code ${room.code.split('').join(' ')}`}>
          {room.code}
        </h1>
        <div className="invite">
          <button className="btn btn-primary" onClick={copy}>
            <IconCopy /> Copy invite link
          </button>
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button className="icon-btn share-btn" onClick={share} aria-label="Share">
              <IconShare />
            </button>
          )}
        </div>

        <ul className="seats" aria-label="Players">
          {seats.map((s, i) => (
            <li key={i} className={`seat${i === you ? ' me' : ''}${!s.isBot && !s.connected ? ' away' : ''}`}>
              <PlayerMedallion seat={i} size={44} />
              <span className="seat-name">{s.name}</span>
              {i === host ? (
                <span className="seat-tag">Host</span>
              ) : s.isBot && s.botLevel ? (
                <span className="seat-tag">{LEVEL_LABEL[s.botLevel]}</span>
              ) : null}
              {isHost && i !== host && (
                <button className="seat-x" aria-label={`Remove ${s.name}`} onClick={() => client.removeSeat(i)}>
                  <IconClose size={14} />
                </button>
              )}
            </li>
          ))}
          {isHost &&
            Array.from({ length: MAX_PLAYERS - seats.length }, (_, k) => (
              <AddSeat key={`open${k}`} disabled={full} pulse={!enough} onAdd={(l) => client.addBot(l)} />
            ))}
        </ul>

        {isHost ? (
          <>
            <div className="seg lobby-holes" role="radiogroup" aria-label="Holes">
              {HOLE_CHOICES.map((h) => (
                <button
                  key={h}
                  role="radio"
                  aria-checked={holes === h}
                  className={`seg-btn${holes === h ? ' on' : ''}`}
                  onClick={() => {
                    setHoles(h);
                    save(KEYS.onlineHoles, h);
                  }}
                >
                  {h} holes
                </button>
              ))}
            </div>
            <button className="btn btn-gold btn-big wide-btn" disabled={!enough} onClick={() => client.start(holes)}>
              Tee off
            </button>
          </>
        ) : you === null ? (
          <p className="lobby-wait">The table is full. You can watch once the game starts.</p>
        ) : (
          <p className="lobby-wait">
            <span className="spinner small" aria-hidden="true" /> Waiting for the host…
          </p>
        )}
        <div className="lobby-foot">
          <button className="link-btn ink" onClick={onHome}>
            Home
          </button>
          {you !== null && (
            <button
              className="link-btn ink"
              onClick={() => {
                client.leave();
                onHome();
              }}
            >
              Leave table
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
